import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

export function AppPage({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`app-page ${className}`}>{children}</div>;
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-7 flex flex-col gap-5 border-b border-app-line pb-6 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        {eyebrow ? <p className="app-kicker mb-2">{eyebrow}</p> : null}
        <h1 className="font-display text-3xl font-semibold leading-tight text-app-ink sm:text-4xl">{title}</h1>
        {description ? <p className="mt-2 max-w-3xl text-sm leading-6 text-app-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

export function SectionHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 border-b border-app-line px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h2 className="text-base font-semibold text-app-ink">{title}</h2>
        {description ? <p className="mt-1 text-sm leading-5 text-app-muted">{description}</p> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export function Panel({ children, className = '', raised = false, ...props }: HTMLAttributes<HTMLDivElement> & { raised?: boolean }) {
  return <div className={`${raised ? 'app-panel-raised' : 'app-panel'} ${className}`} {...props}>{children}</div>;
}

export function MetricTile({
  label,
  value,
  detail,
  tone = 'neutral',
}: {
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  tone?: 'neutral' | 'positive' | 'warning' | 'critical' | 'accent';
}) {
  const toneClasses = {
    neutral: 'border-app-line',
    positive: 'border-app-positive',
    warning: 'border-app-warning',
    critical: 'border-app-critical',
    accent: 'border-app-accent',
  };
  return (
    <div className={`border-l-2 bg-app-surface px-4 py-3 ${toneClasses[tone]}`}>
      <p className="app-kicker">{label}</p>
      <p className="app-tabular mt-2 text-2xl font-semibold text-app-ink">{value}</p>
      {detail ? <div className="mt-1 text-xs leading-5 text-app-muted">{detail}</div> : null}
    </div>
  );
}

export function StatusTag({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'positive' | 'warning' | 'critical' | 'accent' | 'info';
}) {
  const styles = {
    neutral: 'border-app-line-strong text-app-muted',
    positive: 'border-app-positive text-app-positive',
    warning: 'border-app-warning text-app-warning',
    critical: 'border-app-critical text-app-critical',
    accent: 'border-app-accent bg-app-accent text-app-accent-text',
    info: 'border-app-focus text-app-focus',
  };
  return <span className={`inline-flex min-h-7 items-center border px-2 font-mono text-[11px] font-semibold uppercase ${styles[tone]}`}>{children}</span>;
}

export function ProgressBar({
  label,
  value,
  valueText,
  tone = 'accent',
}: {
  label: string;
  value: number;
  valueText?: string;
  tone?: 'accent' | 'positive' | 'warning' | 'critical';
}) {
  const bars = {
    accent: 'bg-app-accent',
    positive: 'bg-app-positive',
    warning: 'bg-app-warning',
    critical: 'bg-app-critical',
  };
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div>
      <div className="mb-2 flex items-end justify-between gap-4">
        <span className="text-sm font-semibold text-app-ink">{label}</span>
        <span className="font-mono text-xs text-app-muted">{valueText ?? `${clamped}%`}</span>
      </div>
      <div className="h-2 overflow-hidden bg-app-line" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={clamped} aria-valuetext={valueText}>
        <div className={`h-full transition-[width] duration-500 ${bars[tone]}`} style={{ width: `${clamped}%` }} />
      </div>
    </div>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="border border-dashed border-app-line-strong bg-app-canvas px-6 py-12 text-center">
      <Icon className="mx-auto h-7 w-7 text-app-muted" aria-hidden="true" />
      <h2 className="mt-4 text-base font-semibold text-app-ink">{title}</h2>
      <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-app-muted">{description}</p>
      {action ? <div className="mt-5 flex justify-center">{action}</div> : null}
    </div>
  );
}

export function IconButton({ label, className = '', children, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={`app-focus-ring inline-flex min-h-10 min-w-10 items-center justify-center border border-app-line-strong text-app-muted transition hover:border-app-ink hover:bg-app-canvas hover:text-app-ink ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}
